import React, {
  ReactNode,
  createContext,
  useCallback,
  useState,
  useMemo,
} from 'react';

import ModalRoot from 'components/core/modal/Root';
import { ModalToastProvider } from 'components/shared/DesignSystem/ModalToast/ModalToastContext';
import { Modal } from 'components/core/modal/constants';

type ComponentName = (typeof Modal)[keyof typeof Modal];
type ComponentProps = Record<string, unknown>;

type ModalState = {
  componentName: string | null;
  props: ComponentProps;
  showModal: (name: ComponentName, props?: ComponentProps) => void;
  hideModal: () => void;
  Modal: typeof Modal;
};

const initialState: ModalState = {
  componentName: null,
  props: {},
  showModal: () => {},
  hideModal: () => {},
  Modal,
};

const ModalContext = createContext(initialState);

const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<
    Pick<ModalState, 'componentName' | 'props'>
  >({
    componentName: null,
    props: {},
  });

  const showModal = useCallback(
    (componentName: ComponentName, props = {}) => {
      setState({
        ...state,
        componentName,
        props,
      });
    },
    [state],
  );

  const hideModal = useCallback(() => {
    setState({
      ...state,
      componentName: null,
      props: {},
    });
  }, [state]);

  const modalProviderValue = useMemo(
    () => ({ ...state, hideModal, showModal, Modal }),
    [state, hideModal, showModal],
  );

  return (
    <ModalContext.Provider value={modalProviderValue}>
      <ModalToastProvider>
        <ModalRoot />
        {children}
      </ModalToastProvider>
    </ModalContext.Provider>
  );
};

const ModalConsumer = ModalContext.Consumer;

export { ModalContext, ModalProvider, ModalConsumer };
